import React from 'react';
import {
    AddCircle as AddCircleIcon,
    Process as ProcessIcon,
    QuoteIcon
} from '@markinson/uicomponents-v2/SvgIcons/';
import PrintIcon from 'assets/Print';
import FastEntry from '../../common/FastEntry';
import { DataGrid } from 'devextreme-react';
import { ActionBarContext } from 'utils/ActionBarContextProvider';
import { Paper, withStyles } from '@material-ui/core';
import styles from './Lines.styles';
import { ILinesHandle, ILinesProperties } from './Lines.properties';
import { createDataSource } from 'components/common/DataGridDevEx/DataGrid.hooks';
import { isNull } from 'utils/utils';
import DynamicDataGrid from 'components/common/DataGridDevEx/DynamicDataGrid';
import { columns, FastEntryEnabledStatus, FieldName } from './constants';
import { IFastEntryFieldChangedQuery, IFastEntryHandle, IFastLineResponse } from 'components/common/FastEntry/FastEntry.properties';
import { useCalculatePurchaseOrderLine, useDeletePurchaseOrderLine, addLine, useCalculateFastEntryDetails, useRetrievePurchaseOrdersLines, useUpdatePurchaseOrderLine, useRetrievePurchaseOrdersSummary } from 'api/purchaseOrders/purchaseOrders';
import { IPurchaseOrderGridLineFacade, ICreateFastLineFacade } from 'api/swaggerTypes';
import { usePOContextSelector, usePODispatch } from '../PurchaseOrders.context';
import { isSuccess } from 'api/utils';
import { HotKeyNames } from 'utils/constants';
import BOMDialog from 'components/common/BOMDialog';
import { usePriceOverrideAndKitValidations } from '../PurchaseOrders.hooks';

const PrintIconStyled = (props) => <PrintIcon {...props} style={{ width: 35, height: 35, margin: 3 }} />;

const Lines = (props: ILinesProperties, ref: React.Ref<ILinesHandle>) => {
    const { classes, selectedPurchaseOrder, changeSelectedTab } = props;
    const { setActionBar } = React.useContext(ActionBarContext);
    const contextDispatch = usePODispatch();
    const fetchLinesMutation = useRetrievePurchaseOrdersLines();
    const deleteLineMutation = useDeletePurchaseOrderLine();
    const calculateLineMutation = useCalculatePurchaseOrderLine();
    const updateLineMutation = useUpdatePurchaseOrderLine();
    const { mutateAsync: addLineMutateAsync } = addLine();
    const { mutateAsync: calculateFastEntryDetailsMutateAsync } = useCalculateFastEntryDetails();

    const dataGridInnerRef = React.useRef<DataGrid>();
    const FastEntryRef = React.useRef<IFastEntryHandle>(null);
    const POSummary = usePOContextSelector<'SummaryPanelData'>((state) => state.SummaryPanelData);
    const fetchPOSummary = useRetrievePurchaseOrdersSummary(usePOContextSelector<'PurchaseOrder'>((state) => state.PurchaseOrder));

    const {
        multipleBomOpen,
        bomList,
        kitStatus,
        selectedBomCode,
        multipleBomDialogApi,
        validate,
        setKitStatus,
        setBomList,
        setSelectedBomCode,
        setMultipleBomState,
    } = usePriceOverrideAndKitValidations();

    const centerIcons = [
        {
            label: 'Print',
            Icon: PrintIconStyled,
            action: 'Print',
            disabled: !(selectedPurchaseOrder && Number(selectedPurchaseOrder) !== 0) || fetchLinesMutation?.data?.PurchaseOrdersLines?.length === 0,
            hotkey: HotKeyNames.CtrlP
        },
        {
            label: 'Load',
            Icon: QuoteIcon,
            action: 'Load',
            disabled: POSummary?.Status !== 'Preparing',
            hotkey: HotKeyNames.CtrlL
        }
    ];
    const rightIcons = [
        {
            label: 'New Order',
            Icon: AddCircleIcon,
            action: 'NewOrder',
            iconStyle: !isNull(selectedPurchaseOrder) ? { fill: 'rgba(0, 0, 0, 1)' } : undefined,
            hotkey: HotKeyNames.CtrlO
        },
        {
            label: 'Process',
            Icon: ProcessIcon,
            action: 'Process',
            disabled: isNull(selectedPurchaseOrder) || POSummary?.PurchaseOrderStatus?.toLowerCase() === 'x' || POSummary?.Status?.toLowerCase() === 'complete',
            iconStyle: !isNull(selectedPurchaseOrder) && POSummary?.PurchaseOrderStatus?.toLowerCase() !== 'x' && POSummary?.Status?.toLowerCase() !== 'complete' ? { fill: 'rgba(109, 217, 0, 1)' } : undefined,
        }
    ];

    React.useEffect(
        () => {
            setActionBar({
                leftIcons: [],
                centerIcons,
                rightIcons
            });
        },
        [selectedPurchaseOrder, POSummary, fetchLinesMutation?.data]
    );

    const deleteLine = React.useCallback(
        async (lineNumber: number) => {
            if (lineNumber && lineNumber !== 0 && selectedPurchaseOrder) {
                const response = await deleteLineMutation.mutateAsync({
                    PurchaseOrder: selectedPurchaseOrder || '',
                    LineNumber: lineNumber
                });

                if (response?.Status) {
                    refreshDataGrid();
                    fetchPOSummary.refetch();
                }
            }
        },
        [selectedPurchaseOrder]
    );

    const fetchLines = React.useCallback(
        async (BatchPage, BatchSize, Sort) => {
            if (selectedPurchaseOrder !== undefined && selectedPurchaseOrder !== null && selectedPurchaseOrder !== '' && selectedPurchaseOrder.toString() !== 'NaN') {
                try {
                    const lines = await fetchLinesMutation.mutateAsync({
                        PurchaseOrder: selectedPurchaseOrder || '',
                        urlQueryParams: {
                            BatchPage,
                            BatchSize,
                            Sort
                        }
                    });

                    const processedLines = lines?.PurchaseOrdersLines?.map((line) => ({
                        ...line,
                        DisableEditAction: !(line?.EnableEdit) || line?.OrderedQuantity === 0,
                        DisableDeleteAction: !(line?.EnableEdit) || line?.OrderedQuantity === 0,
                        DisableLine: line?.OrderedQuantity === 0
                    }));

                    return processedLines;
                } catch (error) {
                    console.error('Error fetching lines:', error);

                    return null;
                }
            } else {
                return null;
            }
        },
        [selectedPurchaseOrder]
    );

    const linesDataSource = React.useMemo(
        () => createDataSource(
            'LineNumber',
            { fetch: fetchLines, update: updatePurchaseOrderLine },
        ),
        [fetchLines]
    );

    const detailOnClick = React.useCallback(
        ({ data }) => {
            if (POSummary?.PurchaseOrderStatus?.toLowerCase() !== 'x' && !data?.DisableLine) {
                changeSelectedTab('LineDetails', {
                    search: {
                        PurchaseOrder: data.PurchaseOrder,
                        LineNumber: data.LineNumber
                    }
                });
                contextDispatch({
                    setPurchaseOrder: data.PurchaseOrder,
                    setLineNumber: data.LineNumber
                });
            }
        },
        [changeSelectedTab, POSummary]
    );

    const refreshDataGrid = React.useCallback(
        async () => {
            await linesDataSource?.reload();
            dataGridInnerRef.current?.instance?.refresh();
            dataGridInnerRef.current?.instance?.cancelEditData();
        },
        [linesDataSource]
    );

    const getLinesCount = React.useCallback(
        () => {

            return fetchLinesMutation?.data?.PurchaseOrdersLines?.length;
        },
        [fetchLinesMutation?.data]
    );

    React.useImperativeHandle(
        ref,
        () => ({
            refreshDataGrid,
            getLinesCount,
            fastEntryResetToDefault: () => {
                FastEntryRef?.current?.resetToDefault();
            }
        }),
        [refreshDataGrid, getLinesCount]
    );

    async function calculatePurchaseOrderLine(line: IPurchaseOrderGridLineFacade, field: keyof IPurchaseOrderGridLineFacade): Promise<any> {
        const response = await calculateLineMutation.mutateAsync({
            ...line,
            PurchaseOrder: selectedPurchaseOrder || '',
            LineNumber: line?.LineNumber,
            urlQueryParams: {
                ChangedField: field,
            }
        });

        if (response?.Status) {
            if (!isNull(response?.PurchaseOrderGridLine?.inlineObject)) {
                return { line: response.PurchaseOrderGridLine?.inlineObject, status: response.Status };
            } else {
                return { line, status: response.Status };
            }
        } else {
            return { line: {}, status: false };
        }
    }

    async function updatePurchaseOrderLine(data: IPurchaseOrderGridLineFacade): Promise<any> {
        const response = await updateLineMutation.mutateAsync({
            PurchaseOrderLine: { ...data },
            PurchaseOrder: selectedPurchaseOrder || '',
            LineNumber: data?.LineNumber
        });

        if (response?.Status) {
            refreshDataGrid();
            fetchPOSummary.refetch();
        } else {
            dataGridInnerRef.current.instance.cancelEditData();
        }
    }

    const handleOnSaving = (e) => {
        e.cancel = true;
        const rowData = (e.changes && e.changes.length) ? e.changes[0].data : null;

        if (rowData) {
            linesDataSource.store().update(rowData.LineNumber, rowData);
        } else {
            dataGridInnerRef.current.instance.cancelEditData();
        }
    };

    const onEditorPreparing = (e) => {
        if (e.dataField === 'OrderedQuantity' && e.row.data.OrderedQuantityReadOnly) {
            e.editorOptions.disabled = true;
        }
        if (e.dataField === 'BackorderedQuantity' && e.row.data.BackorderedQuantityReadOnly) {
            e.editorOptions.disabled = true;
        }
        if (e.dataField === 'DueDate' && e.row.data.DueDateReadOnly) {
            e.editorOptions.disabled = true;
        }
        if (e.dataField === 'Price' && e.row.data.PriceReadOnly) {
            e.editorOptions.disabled = true;
        }
        if (e.dataField === 'SupplierProduct' && e.row.data.SupplierProductReadOnly) {
            e.editorOptions.disabled = true;
        }
    };

    const onApply = async (data: ICreateFastLineFacade) => {
        try {
            const result = await addLineMutateAsync({
                ...data,
                PurchaseOrder: selectedPurchaseOrder,
                PurchaseOrderLine: {
                    OrderedQuantity: data?.OrderedQuantity,
                    Price: data?.DiscountedPrice,
                    ProductId: data?.ProductId,
                }
            });

            if (isSuccess(result)) {
                dataGridInnerRef.current.instance.refresh();
                FastEntryRef.current.resetToDefault();
                fetchPOSummary.refetch();
            }

        } catch (err) {
            throw err;
        }
    };

    const onFastEntryFieldChanged = async (data: IFastEntryFieldChangedQuery): Promise<IFastLineResponse> => {
        const response = await calculateFastEntryDetailsMutateAsync({
            ...data,
            PurchaseOrder: selectedPurchaseOrder,
            urlQueryParams: {
                ChangedField: FieldName[data?.field]
            }
        });

        validate({
            Forms: response.Forms,
            BomCode: response.PurchaseOrderFastLine?.BomCode,
            KitDetail: response.PurchaseOrderFastLine?.KitDetail,
            KitStatus: response.PurchaseOrderFastLine?.KitStatus,
            OverrideDetails: response.PurchaseOrderFastLine,
            field: data?.field,
            multipleBomDialogApi: 'calculateFastEntry'
        });

        if (response.PurchaseOrderFastLine && response.PurchaseOrderFastLine.BomCode) {
            setSelectedBomCode(response.PurchaseOrderFastLine.BomCode);
        }

        return { FastLineDetails: { ...response?.PurchaseOrderFastLine, DiscountedPrice: response?.PurchaseOrderFastLine?.Price }, ...response };
    };

    const clearKittingDetail = () => {
        setKitStatus('');
        setBomList([]);
        setSelectedBomCode('');
    };

    const onGoBack = () => {
        if (FastEntryRef.current) {
            FastEntryRef.current.resetToDefault();
        }
        clearKittingDetail();
    };

    const handleActionClick = (status: 'c' | 'k') => {
        setMultipleBomState(false);
        setKitStatus(status);

        if (!status) {
            if (multipleBomDialogApi === 'calculateFastEntry') {
                onGoBack();
            }

            return;
        }

        switch (multipleBomDialogApi) {
            case 'calculateFastEntry': {
                if (status === 'c') {
                    if (FastEntryRef.current) {
                        FastEntryRef.current.disablePriceField();
                        FastEntryRef.current.calculateFastLineEntry({ KitStatus: 'c', BomCode: selectedBomCode });
                    }
                } else if (status === 'k') {
                    if (FastEntryRef.current) {
                        FastEntryRef.current.calculateFastLineEntry({ KitStatus: 'k', BomCode: selectedBomCode });
                    }
                } else {
                    onGoBack();
                }

                break;
            }

            default:
        }
    };

    return (
        <div className={classes.parentContainer}>
            <Paper className={classes.fastEntryContainer}>
                <FastEntry
                    innerRef={FastEntryRef}
                    config={{
                        priceFieldAsLookup: false,
                        disableFastEntry: (!selectedPurchaseOrder || !FastEntryEnabledStatus.includes(POSummary?.Status)),
                        module: 'purchaseOrders',
                        params: { Warehouse: POSummary?.Warehouse }
                    }}
                    handlers={{
                        onApply,
                        onFastEntryFieldChanged
                    }}

                />
            </Paper>
            <Paper className={classes.gridContainer}>
                <DynamicDataGrid
                    dataGridClassName={classes.grid}
                    columns={columns}
                    allowHeaderFilting={false}
                    calculateFieldValue={calculatePurchaseOrderLine}
                    allowUpdating={true}
                    detailOnClick={detailOnClick}
                    gridProps={{
                        dataSource: linesDataSource,
                        dataGridRef: dataGridInnerRef,
                        sorting: { mode: 'single' },
                        disableDrillDown: POSummary?.PurchaseOrderStatus?.toLowerCase() === 'x',
                        disableEditing: false,
                        disableDelete: false,
                        onSaving: handleOnSaving,
                        onEditorPreparing: onEditorPreparing,
                    }}
                    onDelete={deleteLine}
                />
            </Paper>
            <BOMDialog
                open={multipleBomOpen}
                onClose={() => setMultipleBomState(false)}
                data={bomList}
                kitStatus={kitStatus ? kitStatus.toLowerCase() : ''}
                onActionClick={handleActionClick}
                onSelectedRowChanged={setSelectedBomCode}
            />
        </div>
    );
};

export default withStyles(styles, { index: 1 })(React.memo(React.forwardRef(Lines)));
